import React, { useState, useEffect } from "react";
import LoginHeader from "./components/LoginHeader.js";
import AADHeader from "./components/AADHeader.js";
import SideNavigation from "./components/SideNavigation.js";
import { InputText } from "primereact/inputtext";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import { appInsights } from "./AppInsights";

const GLOSSARY_TERMS = [
  { term: "BRSR", def: "Business Responsibility and Sustainability Reporting — SEBI-mandated ESG disclosure framework for the top 1,000 listed companies in India." },
  { term: "CBAM", def: "Carbon Border Adjustment Mechanism — the EU's carbon tariff on imports of carbon-intensive goods (steel, cement, aluminium, fertilisers, electricity, hydrogen)." },
  { term: "CDP", def: "Carbon Disclosure Project — a global disclosure system where companies report environmental impacts (climate, water, forests) and receive an A–F score." },
  { term: "Carbon Neutral", def: "Balancing emitted CO₂ with an equivalent amount offset or removed. Unlike Net Zero, it allows unlimited use of offsets and typically covers CO₂ only." },
  { term: "CSRD", def: "Corporate Sustainability Reporting Directive — EU law requiring large and listed companies to report sustainability information under the ESRS standards." },
  { term: "Double Materiality", def: "The CSRD principle that a topic is material if it affects the company financially (financial materiality) or if the company impacts people/environment (impact materiality)." },
  { term: "Emission Factor", def: "A coefficient converting activity data (kWh, litres, km) into greenhouse gas emissions, e.g. kgCO₂e per kWh of grid electricity." },
  { term: "ESG", def: "Environmental, Social and Governance — the three pillars used to evaluate an organisation's sustainability and ethical performance." },
  { term: "ESRS", def: "European Sustainability Reporting Standards — the twelve detailed standards (ESRS 1–2, E1–E5, S1–S4, G1) that CSRD reports must follow." },
  { term: "EUDR", def: "EU Deforestation Regulation — requires companies placing commodities like soy, palm oil, timber, coffee or cattle on the EU market to prove they are deforestation-free." },
  { term: "EU Taxonomy", def: "The EU's classification system defining which economic activities count as environmentally sustainable, used for reporting eligible and aligned revenue, CapEx and OpEx." },
  { term: "GHG Protocol", def: "The most widely used greenhouse gas accounting standard, defining Scopes 1, 2 and 3 and the rules EcoPrism follows for emission calculations." },
  { term: "GRI", def: "Global Reporting Initiative — a widely adopted voluntary framework for reporting economic, environmental and social impacts." },
  { term: "LCA", def: "Life Cycle Assessment — analysis of a product's environmental impact across its full life: raw materials, production, use and end-of-life." },
  { term: "Materiality Assessment", def: "The structured process of identifying which ESG topics matter most to a business and its stakeholders, used to scope reporting." },
  { term: "Net Zero", def: "Reducing greenhouse gas emissions as close to zero as possible (typically ≥90% under SBTi) with only the residual balanced by permanent removals." },
  { term: "NFRD", def: "Non-Financial Reporting Directive — the EU's predecessor to CSRD, in force until CSRD phase-in began." },
  { term: "Omnibus", def: "The EU Omnibus package amending CSRD/CSDDD — raising size thresholds, delaying waves and simplifying ESRS requirements." },
  { term: "PPA", def: "Power Purchase Agreement — a long-term contract to buy electricity, often renewable, directly from a generator at an agreed price." },
  { term: "REC", def: "Renewable Energy Certificate — a tradable certificate proving 1 MWh of electricity was generated from renewable sources; used in market-based Scope 2 accounting." },
  { term: "SASB", def: "Sustainability Accounting Standards Board — industry-specific standards (now under IFRS/ISSB) identifying financially material ESG topics for 77 industries." },
  { term: "SBTi", def: "Science Based Targets initiative — validates that corporate emission-reduction targets are consistent with limiting warming to 1.5°C." },
  { term: "Scope 1", def: "Direct emissions from sources a company owns or controls — fuel combustion in facilities and vehicles, process and fugitive emissions." },
  { term: "Scope 2", def: "Indirect emissions from purchased energy — electricity, steam, heating and cooling. Reported location-based and market-based." },
  { term: "Scope 3", def: "All other indirect emissions across the value chain — purchased goods, business travel, commuting, logistics, use of sold products and more (15 categories)." },
  { term: "SFDR", def: "Sustainable Finance Disclosure Regulation — EU rules requiring financial market participants to disclose how they consider sustainability risks." },
  { term: "TCFD", def: "Task Force on Climate-related Financial Disclosures — a four-pillar framework (governance, strategy, risk management, metrics & targets) for climate risk reporting." },
  { term: "tCO₂e", def: "Tonnes of CO₂ equivalent — the common unit expressing all greenhouse gases by their global-warming potential relative to CO₂." },
  { term: "VSME", def: "Voluntary SME standard — the simplified voluntary reporting standard for small and medium enterprises outside CSRD's mandatory scope." },
  { term: "XBRL", def: "eXtensible Business Reporting Language — the machine-readable tagging format required for digital CSRD/ESEF filings." },
];

const FAQS = [
  { q: "Where does EcoPrism get its emission factors?", a: "Emission factors come from recognised public sources (IEA, DEFRA, EPA and national grids) and are matched automatically to your activity data based on country, year and energy type during report runs." },
  { q: "What is the difference between location-based and market-based Scope 2?", a: "Location-based uses the average grid emission factor for where you consume electricity. Market-based reflects your contracts — PPAs, RECs and green tariffs. EcoPrism calculates both when contract data is configured." },
  { q: "How do I add a new site or legal entity?", a: "Go to Company Details in the side navigation. Legal entities carry country, industry sector, revenue and headcount; site locations sit under entities and carry the metering and connector configuration." },
  { q: "How does data get into the platform?", a: "Three ways: file uploads (Excel/CSV per ESG metric), configured connectors that pull from your systems automatically, and surveys sent to colleagues or suppliers for data you don't hold centrally." },
  { q: "Why don't my dashboard numbers match my uploaded file?", a: "Dashboards show calculated emissions after factor matching, unit conversion and deduplication — not raw activity values. Check Report History for the run that processed your file and its line-level results." },
  { q: "Which reporting frameworks does EcoPrism support?", a: "CSRD/ESRS and BRSR report generation are built in, with dashboards and data structured to align with the GHG Protocol, and mappings toward SASB, TCFD and CDP disclosures — see the Reporting Framework Guides page." },
  { q: "Who can see and edit my company's data?", a: "Access is scoped to your company and controlled by roles. Administration rights (managing users, org structure and configuration) are limited to users granted those roles by your admin." },
  { q: "Something looks wrong — how do I get help?", a: "Ask EcoBot (bottom-right on the home page) for instant answers, or contact support@ecoprism.com for anything the bot can't resolve." },
];

export default function Glossary() {
  const [isSidebarOpen, setIsSidebarOpen] = useState(window.innerWidth >= 768);
  const [search, setSearch] = useState("");
  const [openFaq, setOpenFaq] = useState(null);

  const toggleSidebar = () => setIsSidebarOpen((prev) => !prev);

  useEffect(() => {
    appInsights.trackPageView();
    const handleResize = () => setIsSidebarOpen(window.innerWidth >= 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const renderHeader = () => {
    const loginType = localStorage.getItem("loginType");
    if (loginType === "AAD") {
      return <AADHeader toggleSidebar={toggleSidebar} isSidebarOpen={isSidebarOpen} heading="" />;
    }
    return <LoginHeader toggleSidebar={toggleSidebar} isSidebarOpen={isSidebarOpen} heading="" />;
  };

  const filteredTerms = GLOSSARY_TERMS.filter(
    (t) =>
      t.term.toLowerCase().includes(search.toLowerCase()) ||
      t.def.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div id="wrapper">
      {renderHeader()}
      <SideNavigation isSidebarOpen={isSidebarOpen} toggleSidebar={toggleSidebar} />
      <div
        id="page-content-wrapper"
        className={isSidebarOpen ? "page-content-wrapper-pd-0" : "page-wrapper"}
      >
        <div className="container formpd">
          <div className="col-md-12 flex mt-50">
            <div className="col-md-8 pd-0">
              <p className="home-head">Glossary &amp; FAQ</p>
              <p className="glossary-intro">
                Quick reference for ESG terminology, acronyms and common platform questions.
              </p>
            </div>
            <div className="col-md-4 pd-0">
              <InputText
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search terms..."
                className="wd-100 form-control"
              />
            </div>
          </div>

          <div className="col-md-12 pd-0 glossary-grid">
            {filteredTerms.map((t) => (
              <div className="glossary-card" key={t.term}>
                <p className="glossary-term">{t.term}</p>
                <p className="glossary-def">{t.def}</p>
              </div>
            ))}
            {filteredTerms.length === 0 && (
              <p className="glossary-empty">No terms match “{search}”.</p>
            )}
          </div>

          <div className="col-md-12 flex mt-50">
            <div className="col-md-12 pd-0">
              <p className="home-head">Frequently Asked Questions</p>
            </div>
          </div>
          <div className="col-md-12 pd-0 mb-25">
            {FAQS.map((f, i) => (
              <div className="faq-item" key={i}>
                <button
                  type="button"
                  className="faq-question"
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                >
                  <span>{f.q}</span>
                  {openFaq === i ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </button>
                {openFaq === i && <p className="faq-answer">{f.a}</p>}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
